import React, { useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Alert,
  Tooltip,
  Card,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../utils/axios-instance";
import { ServerRoutes } from "../routes";
import { useParams } from "react-router-dom";

interface User {
  id: number;
  telegramId: string;
  username: string;
  createdAt: string;
  children: User[];
  parent: User;
}

const UserTreeItem = ({
  user,
  level = 0,
  isLastChild = false,
}: {
  user: User;
  level?: number;
  isLastChild?: boolean;
}) => {
  const [open, setOpen] = useState(level === 0);
  const hasChildren = user.children && user.children.length > 0;

  // Constants for tree visualization - 4px indent
  const indent = 4; // Small indentation between levels
  const nodeSize = 24; // Size of the ID circle

  return (
    <Box sx={{ position: "relative", ml: level * indent, mb: 1 }}>
      {/* Card container */}
      <Card
        elevation={0}
        sx={{
          width: "100%",
          border: "1px solid #eee",
          borderRadius: 2,
          p: 1.5,
          position: "relative",
          transition: "all 0.2s",
          "&:hover": {
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            borderColor: "#ddd",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {/* ID circle */}
          <Box
            sx={{
              minWidth: nodeSize,
              width: nodeSize,
              height: nodeSize,
              bgcolor: "rgba(0,0,0,0.04)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #eee",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {user.id}
            </Typography>
          </Box>

          {/* User info */}
          <Box
            sx={{
              flexGrow: 1,
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              gap: { xs: 0.5, sm: 2 },
            }}
          >
            <Typography fontWeight={500} variant="body1">
              {user.username || `User ${user.id}`}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography color="text.secondary" variant="body2">
                TG: {user.telegramId}
              </Typography>
              <Typography color="text.secondary" variant="body2">
                Joined: {new Date(user.createdAt).toLocaleDateString()}
              </Typography>
              {hasChildren && (
                <Typography color="primary" variant="body2">
                  {user.children.length}{" "}
                  {user.children.length === 1 ? "child" : "children"}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Expand/collapse button */}
          {hasChildren && (
            <IconButton
              onClick={() => setOpen(!open)}
              size="small"
              sx={{
                p: 0.5,
                border: "1px solid #f0f0f0",
                backgroundColor: open ? "rgba(0,0,0,0.04)" : "transparent",
              }}
            >
              {open ? (
                <ExpandLessIcon fontSize="small" />
              ) : (
                <ExpandMoreIcon fontSize="small" />
              )}
            </IconButton>
          )}
        </Box>

        {/* Parent/Referrer section */}
        {user.parent && (
          <Box
            sx={{
              mt: 1.5,
              pt: 1.5,
              borderTop: "1px dashed #eee",
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.primary"
              sx={{ mb: 1, display: "block", fontWeight: "bold" }}
            >
              Referrer:
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                bgcolor: "rgba(0,0,0,0.01)",
                p: 1.5,
                borderRadius: 1,
                border: "1px solid #f0f0f0",
              }}
            >
              {/* Header with ID and username */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    bgcolor: "rgba(0,0,0,0.04)",
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid #eee",
                  }}
                >
                  <Typography variant="caption" sx={{ fontSize: "0.7rem" }}>
                    {user.parent.id}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={500}>
                  {user.parent.username || `User ${user.parent.id}`}
                </Typography>
              </Box>

              {/* Parent details */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, pl: 0.5 }}>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  fontSize="0.875rem"
                >
                  TG: {user.parent.telegramId}
                </Typography>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  fontSize="0.875rem"
                >
                  Joined: {new Date(user.parent.createdAt).toLocaleDateString()}
                </Typography>
                {user.parent.children && user.parent.children.length > 0 && (
                  <Typography
                    color="primary"
                    variant="body2"
                    fontSize="0.875rem"
                  >
                    {user.parent.children.length}{" "}
                    {user.parent.children.length === 1 ? "child" : "children"}
                  </Typography>
                )}
                {user.parent.parent && (
                  <Typography
                    color="text.secondary"
                    variant="body2"
                    fontSize="0.875rem"
                  >
                    Has parent: ID #{user.parent.parent.id}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Card>

      {/* Children */}
      {hasChildren && open && (
        <Box sx={{ position: "relative" }}>
          <Box sx={{ mt: 1 }}>
            {user.children.map((child, index) => (
              <UserTreeItem
                key={child.id}
                user={child}
                level={level + 1}
                isLastChild={index === user.children.length - 1}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const TreeContainer = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      sx={{
        border: "1px solid #eee",
        borderRadius: 2,
        p: 3,
        background:
          "linear-gradient(to bottom, rgba(250,250,255,0.5), rgba(250,250,255,0))",
        boxShadow: "inset 0 1px 4px rgba(0,0,0,0.02)",
      }}
    >
      {children}
    </Box>
  );
};

export function UserTreeSection() {
  const { userId } = useParams<{ userId?: string }>();

  const { data, isLoading, error, refetch } = useQuery<User[]>({
    queryKey: ["userTree", userId],
    queryFn: async () => {
      try {
        const endpoint = userId
          ? `${ServerRoutes.userTree}/${userId}/tree`
          : `${ServerRoutes.userTree}/tree`;

        const response = await axiosInstance.get(endpoint);
        return response.data;
      } catch (err) {
        console.error("Error fetching user tree:", err);
        throw err;
      }
    },
  });

  if (isLoading) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", mt: 3, opacity: 0.7 }}
      >
        <CircularProgress size={20} thickness={3} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {userId ? `User #${userId} Community` : "User Community"}
          </Typography>
        </Box>

        <Tooltip title="Refresh">
          <IconButton
            onClick={() => refetch()}
            size="small"
            sx={{
              p: 1,
              border: "1px solid #eee",
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error: {(error as Error).message}
        </Alert>
      )}

      <Divider sx={{ mb: 3 }} />

      <Typography color="text.secondary" sx={{ mb: 2 }}>
        {userId
          ? `Showing hierarchical community for User #${userId}`
          : "User community visualization with parent-child relationships"}
      </Typography>

      <TreeContainer>
        {data && data.length > 0 ? (
          <Box>
            {data.map((user, index) => (
              <UserTreeItem
                key={user.id}
                user={user}
                isLastChild={index === data.length - 1}
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ p: 3, textAlign: "center", border: "1px dashed #eee" }}>
            <Typography variant="body2" color="text.secondary">
              {userId
                ? `No hierarchical data available for User #${userId}`
                : "No user community data available"}
            </Typography>
          </Box>
        )}
      </TreeContainer>
    </Box>
  );
}
